import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChipList from '../common/ChipList';
import LLMSuggest from '../common/LLMSuggest';
import AttributeEditor from './AttributeEditor';
import api from '../../services/api';
import type { NewsBeatAttribute, NewsBeatDetail, NewsRereportPolicy, Project } from '../../types';

/**
 * A beat's setup: what it watches, how it filters, and how often it runs.
 *
 * The two cadence controls answer different questions and are deliberately
 * adjacent so the difference is visible:
 *   - "Check for news"  — how often the beat looks.
 *   - "Repeat a story"  — how often an ONGOING story may be restated.
 * Without the second, a beat that checks hourly reports the same earthquake
 * twenty times. With it, more coverage is silently collapsed and only genuine
 * developments get through.
 */

const CADENCES = [
  { minutes: 60, label: 'Every hour' },
  { minutes: 360, label: 'Every 6 hours' },
  { minutes: 720, label: 'Every 12 hours' },
  { minutes: 1440, label: 'Daily' },
  { minutes: 4320, label: 'Every 3 days' },
  { minutes: 10080, label: 'Weekly' },
];

// Two fields behind one control. The last option is NEVER — the
// material-development escape hatch is always on, which is exactly what makes
// "only when there's a new development" a truthful label for it.
const REREPORT: {
  key: string;
  label: string;
  policy: NewsRereportPolicy;
  minutes: number;
  blurb: string;
}[] = [
  {
    key: 'every',
    label: 'Every briefing',
    policy: 'EVERY_RUN',
    minutes: 0,
    blurb: 'Restate ongoing stories every time. Noisy, but nothing is hidden.',
  },
  {
    key: 'daily',
    label: 'At most once a day',
    policy: 'COOLDOWN',
    minutes: 1440,
    blurb: 'A running story is restated at most daily.',
  },
  {
    key: 'weekly',
    label: 'At most once a week',
    policy: 'COOLDOWN',
    minutes: 10080,
    blurb: 'A running story is restated at most weekly.',
  },
  {
    key: 'monthly',
    label: 'At most once a month',
    policy: 'COOLDOWN',
    minutes: 43200,
    blurb: 'A running story is restated at most monthly.',
  },
  {
    key: 'never',
    label: "Only when there's a new development",
    policy: 'NEVER',
    minutes: 0,
    blurb:
      'Never restate. A story reappears only when something has actually happened, and the summary says what.',
  },
];

function rereportKey(policy: NewsRereportPolicy, minutes: number): string {
  if (policy === 'EVERY_RUN') return 'every';
  if (policy === 'NEVER') return 'never';
  const match = REREPORT.find((r) => r.policy === 'COOLDOWN' && r.minutes === minutes);
  return match?.key ?? 'weekly';
}

/** The saved beat, reduced to exactly the fields this form owns. Everything
 *  that decides whether the form is dirty, and whether a change arriving from
 *  the server conflicts with what the user is typing, is compared through this
 *  — so the two questions can never drift apart. */
function seedOf(beat: NewsBeatDetail) {
  return {
    name: beat.name,
    description: beat.description ?? null,
    guidance: beat.guidance ?? null,
    keywords: beat.keywords,
    attributes: beat.attributes,
    interval_minutes: beat.interval_minutes,
    max_articles_per_run: beat.max_articles_per_run,
    lookback_minutes: beat.lookback_minutes ?? null,
    max_passes_per_run: beat.max_passes_per_run,
    project_id: beat.project_id ?? null,
    rereport: rereportKey(beat.rereport_policy, beat.rereport_interval_minutes),
  };
}

// GNews serves 25 articles a page and we will not walk more than ten pages, so
// 250 is the real ceiling — offering 500 would be offering something the server
// silently clamps. The request cost is spelled out because it is the thing the
// user is actually spending: a daily quota, not a slider.
const FETCH_DEPTHS = [
  { articles: 25, label: '25 - a sample', pages: 1 },
  { articles: 50, label: '50', pages: 2 },
  { articles: 100, label: '100 (default)', pages: 4 },
  { articles: 150, label: '150', pages: 6 },
  { articles: 250, label: '250 - as deep as GNews goes', pages: 10 },
];

// `null` means "since the last run", which is the right default for a beat on a
// cadence and useless for a first run — hence the explicit choices.
const LOOKBACKS: { key: string; label: string; minutes: number | null }[] = [
  { key: 'auto', label: 'Since the last run', minutes: null },
  { key: '6h', label: 'Last 6 hours', minutes: 360 },
  { key: '24h', label: 'Last 24 hours', minutes: 1440 },
  { key: '3d', label: 'Last 3 days', minutes: 4320 },
  { key: '7d', label: 'Last 7 days (the maximum)', minutes: 10080 },
];

function lookbackKey(minutes: number | null | undefined): string {
  if (!minutes) return 'auto';
  return LOOKBACKS.find((l) => l.minutes === minutes)?.key ?? 'auto';
}

type BeatSeed = ReturnType<typeof seedOf>;

export default function BeatSetupPanel({
  beat,
  onSaved,
  onRun,
  onReprocess,
  lastBriefingId,
  runState,
}: {
  beat: NewsBeatDetail;
  onSaved: (next: NewsBeatDetail) => void;
  onRun: () => void;
  /** Re-judge an existing pool. Absent on a live beat, where it cannot run. */
  onReprocess?: (briefingId: number) => void;
  lastBriefingId?: number | null;
  /** What the beat's latest briefing is doing, derived server-side. */
  runState: 'idle' | 'queued' | 'running';
}) {
  const [name, setName] = useState(beat.name);
  const [description, setDescription] = useState(beat.description ?? '');
  const [guidance, setGuidance] = useState(beat.guidance ?? '');
  const [keywords, setKeywords] = useState<string[]>(beat.keywords);
  const [attributes, setAttributes] = useState<NewsBeatAttribute[]>(beat.attributes);
  const [interval, setInterval] = useState(beat.interval_minutes);
  const [maxArticles, setMaxArticles] = useState(beat.max_articles_per_run);
  const [lookback, setLookback] = useState(lookbackKey(beat.lookback_minutes));
  const [maxPasses, setMaxPasses] = useState(beat.max_passes_per_run);
  const [rereport, setRereport] = useState(
    rereportKey(beat.rereport_policy, beat.rereport_interval_minutes),
  );
  const [projectId, setProjectId] = useState<number | null>(beat.project_id ?? null);
  const [projects, setProjects] = useState<Project[]>([]);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    api.getProjects().then(setProjects).catch(() => setProjects([]));
  }, []);

  // The form's fields in one shape, so "what the server has" and "what is on
  // screen" are the same kind of thing and can be compared field by field.
  const current = useMemo(
    () => ({
      name,
      description: description || null,
      guidance: guidance || null,
      keywords,
      attributes,
      interval_minutes: interval,
      max_articles_per_run: maxArticles,
      lookback_minutes: LOOKBACKS.find((l) => l.key === lookback)?.minutes ?? null,
      max_passes_per_run: maxPasses,
      project_id: projectId,
      rereport,
    }),
    [
      name,
      description,
      guidance,
      keywords,
      attributes,
      interval,
      maxArticles,
      lookback,
      maxPasses,
      projectId,
      rereport,
    ],
  );
  // Read by the reconcile effect below, which reacts to the SERVER moving and
  // must not re-run on every keystroke.
  const currentRef = useRef(current);
  currentRef.current = current;

  // Compared against the LIVE `beat` prop, not a snapshot taken at mount. A
  // snapshot goes stale the moment anything writes the beat from outside this
  // form — the tuning agent, a keyword chip, the pause toggle in the header —
  // leaving a baseline that disagrees with the server, so Save lights up with
  // nothing edited and then re-PATCHes whatever the form happens to hold.
  const dirty = useMemo(
    () => JSON.stringify(current) !== JSON.stringify(seedOf(beat)),
    [current, beat],
  );

  // ── Reconciling with writes we didn't make ────────────────────────────
  //
  // The tuning agent writes these same fields, and the keyword chips PATCH on
  // every Enter, so a new `beat` can arrive at any moment. Three cases, and
  // they want different answers:
  //
  //   1. It matches what's already on screen (our own chip PATCH coming back) —
  //      adopt the new baseline silently. Raising a banner over the user's own
  //      keystroke would be nonsense.
  //   2. It differs and the form is untouched — adopt it, so the agent's change
  //      is simply visible.
  //   3. It differs and the user has typed — keep the typing and say so. The
  //      edits are the unrecoverable side; the agent's version is still on the
  //      server and one click away.
  const seedRef = useRef(seedOf(beat));
  const [beatMoved, setBeatMoved] = useState(false);
  // Compared against seedRef, which does NOT change when `beat` changes — so
  // this answer is stable across the render that delivers the new prop and
  // doesn't depend on which effect happens to run first.
  const userTypedRef = useRef(false);
  userTypedRef.current =
    JSON.stringify(current) !== JSON.stringify(seedRef.current);

  const adopt = useCallback((seed: BeatSeed) => {
    setName(seed.name);
    setDescription(seed.description ?? '');
    setGuidance(seed.guidance ?? '');
    setKeywords(seed.keywords);
    setAttributes(seed.attributes);
    setInterval(seed.interval_minutes);
    setMaxArticles(seed.max_articles_per_run);
    setLookback(lookbackKey(seed.lookback_minutes));
    setMaxPasses(seed.max_passes_per_run);
    setProjectId(seed.project_id);
    setRereport(seed.rereport);
    seedRef.current = seed;
    setBeatMoved(false);
  }, []);

  useEffect(() => {
    const incoming = seedOf(beat);
    const keys = Object.keys(incoming) as (keyof BeatSeed)[];
    const moved = keys.filter(
      (key) =>
        JSON.stringify(incoming[key]) !== JSON.stringify(seedRef.current[key]),
    );
    if (moved.length === 0) return;

    const conflicting = moved.filter(
      (key) =>
        JSON.stringify(incoming[key]) !== JSON.stringify(currentRef.current[key]),
    );
    if (conflicting.length === 0) {
      seedRef.current = incoming; // case 1
      return;
    }
    if (userTypedRef.current) {
      setBeatMoved(true); // case 3
      return;
    }
    adopt(incoming); // case 2
  }, [beat, adopt]);

  // Search terms persist the moment a chip is added or removed. The chip input
  // has no Save of its own — you press Enter, the chip appears, and it looks
  // saved — so leaving the screen without hitting the form's Save silently
  // dropped it. PATCHes are chained rather than fired in parallel: picking three
  // suggestions in a row is three near-simultaneous writes of the WHOLE list,
  // and out-of-order arrival would resurrect the shortest one.
  const keywordWrite = useRef<Promise<void>>(Promise.resolve());
  const saveKeywords = (next: string[]) => {
    setKeywords(next);
    keywordWrite.current = keywordWrite.current.then(async () => {
      try {
        setError(null);
        onSaved(await api.updateNewsBeat(beat.id, { keywords: next }));
      } catch (e) {
        // No revert — the chips stay on screen and the form goes dirty, so Save
        // is the retry. Silently rolling back a term the user just typed is the
        // worse failure.
        setError(e instanceof Error ? e.message : String(e));
      }
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    const choice = REREPORT.find((r) => r.key === rereport)!;
    try {
      // Two calls: the beat, then its attribute set (which is replace-the-whole-
      // set). Sequential rather than parallel so a failure in the first doesn't
      // leave attributes saved against un-saved settings.
      await api.updateNewsBeat(beat.id, {
        name: name.trim() || beat.name,
        description: description.trim() || null,
        guidance: guidance.trim() || null,
        keywords,
        interval_minutes: interval,
        max_articles_per_run: maxArticles,
        lookback_minutes:
          LOOKBACKS.find((l) => l.key === lookback)?.minutes ?? null,
        max_passes_per_run: maxPasses,
        rereport_policy: choice.policy,
        rereport_interval_minutes: choice.minutes || beat.rereport_interval_minutes,
        project_id: projectId,
      });
      const next = await api.replaceNewsBeatAttributes(
        beat.id,
        attributes.filter((a) => a.name.trim()),
      );
      setAttributes(next.attributes);
      setSavedAt(Date.now());
      onSaved(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const linkedProject = projects.find((p) => p.id === projectId);

  return (
    <div className="space-y-6 max-w-3xl">
      {error && (
        <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 px-3 py-2 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {beatMoved && (
        <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 flex flex-wrap items-center gap-2">
          <span className="flex-1 min-w-[16rem]">
            These settings changed while you were editing - the tuner wrote a new
            version. Your edits below are untouched: <strong>Save</strong> to keep
            them, or discard them to see what it wrote.
          </span>
          <button
            type="button"
            onClick={() => adopt(seedOf(beat))}
            className="px-2 py-1 rounded border border-amber-400 dark:border-amber-600 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            Discard my edits
          </button>
        </div>
      )}

      {/* Active/Paused used to sit here as well as at the top of the page, one
          as a button and one as a badge. It belongs with the beat's name, next
          to Draft/Live: both answer "what is this beat doing", and neither is a
          setting you edit and Save. */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={onRun}
          // Queued is NOT disabled. A queued briefing whose worker died stays
          // queued, and the only thing that clears it is this endpoint — so
          // disabling here gated the recovery behind the button it disabled.
          // The server owns the rule: it frees a dead run and starts a new one,
          // or 409s if one is genuinely in flight.
          disabled={runState === 'running' || keywords.length === 0}
          title={
            keywords.length === 0
              ? 'Add at least one search term first'
              : runState === 'queued'
                ? 'A briefing is queued - click to retry if its worker died'
                : undefined
          }
          className="px-3 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {
            {
              idle: 'Fetch fresh articles',
              queued: 'Queued - retry',
              running: 'Running…',
            }[runState]
          }
        </button>
        {/* The cheap half of the pair, and the one that gets used most. Named
            for what it DOES rather than for the mechanism: "re-process" told
            you a job would run, not that you would get a clean slate over the
            same articles, which is the thing anyone actually wants after
            changing a filter or adding an attribute. */}
        {beat.mode === 'DRAFT' && onReprocess && lastBriefingId && (
          <button
            onClick={() => onReprocess(lastBriefingId)}
            // Queued is NOT disabled, for the same reason as the button beside
            // it: a briefing whose worker died stays queued forever, and the
            // server already decides — it frees a provably dead job and 409s
            // on one that is genuinely in flight.
            disabled={runState === 'running'}
            title={
              runState === 'queued'
                ? 'A run is queued. If its worker died this starts over; if it is genuinely in flight the server will say so.'
                : "Throw away the last run's stories and judge the same articles again with these settings. No new search, no news-API quota."
            }
            className="px-3 py-1.5 text-sm rounded-md border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50"
          >
            Re-judge the same articles
          </button>
        )}
        {beat.last_run_at && (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            Last scheduled run {new Date(beat.last_run_at).toLocaleString()}
          </span>
        )}
      </div>

      <section className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            What does this beat cover?
          </label>
          {/* `relative` is what LLMSuggest's typeahead variant positions
              against. The sparkles button works on an empty box too — the name
              and search terms below are usually enough to draft from. */}
          <div className="relative">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Energy and utility news affecting Utah, Nevada and Colorado."
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 pr-9 text-sm"
            />
            <LLMSuggest
              variant="typeahead"
              context="news_beat_description"
              label="Beat coverage"
              value={description}
              // Slower and later than the default typeahead. Picking a
              // suggestion REPLACES the whole box, so a dropdown that opens
              // while someone is mid-sentence is an offer to discard what they
              // are writing. The sparkles button is always there on demand.
              minChars={6}
              debounceMs={900}
              additionalContext={{
                ...(name ? { beat_name: name } : {}),
                ...(keywords.length ? { search_terms: keywords.join(', ') } : {}),
                ...(attributes.filter((a) => a.name.trim()).length
                  ? {
                      attributes: attributes
                        .filter((a) => a.name.trim())
                        .map(
                          (a) =>
                            `${a.name} (${a.role.toLowerCase()}${
                              a.values.length ? `: ${a.values.join(', ')}` : ''
                            })`,
                        )
                        .join('; '),
                    }
                  : {}),
                ...(linkedProject?.description
                  ? { niche_description: linkedProject.description }
                  : {}),
              }}
              onSelect={(s) => setDescription(s)}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Used to judge relevance, so write it the way you'd brief a person.
          </p>
        </div>
      </section>

      <section>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Search terms
        </label>
        <ChipList
          values={keywords}
          onChange={saveKeywords}
          placeholder="e.g. solar - Enter to add"
          clearable
          suggestContext="news_keyword"
          suggestLabel="Search terms"
          additionalContext={{
            beat_name: name,
            beat_description: description,
            niche_description: linkedProject?.description ?? '',
          }}
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Matched against article text, any one of them. Concrete words beat
          abstract ones - "solar" finds more than "energy policy". Terms save as
          you add them.
        </p>
      </section>

      <section>
        <AttributeEditor
          attributes={attributes}
          onChange={setAttributes}
          beatName={name}
          beatDescription={description}
        />
      </section>

      <section>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Standing instructions <span className="font-normal">(optional)</span>
        </label>
        <textarea
          value={guidance}
          onChange={(e) => setGuidance(e.target.value)}
          rows={2}
          placeholder="Prefer local reporting over wire copy. Ignore stock-price commentary."
          className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-3 py-2 text-sm"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Honored when writing the briefing, but never used to drop an article -
          that's what filter attributes are for.
        </p>
      </section>

      <section className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Check for news
          </label>
          <select
            value={interval}
            onChange={(e) => setInterval(Number(e.target.value))}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-2 text-sm"
          >
            {CADENCES.map((c) => (
              <option key={c.minutes} value={c.minutes}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Repeat an ongoing story
          </label>
          <select
            value={rereport}
            onChange={(e) => setRereport(e.target.value)}
            className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-2 text-sm"
          >
            {REREPORT.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {REREPORT.find((r) => r.key === rereport)?.blurb}
          </p>
        </div>
      </section>

      {/* Next to the cadence controls on purpose: how far back to look and how
          often to look are one decision. A 21-hour window on a weekly beat
          means six days nobody ever fetched, and that is invisible while these
          two live on different screens. */}
      <section className="space-y-3">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          How much to fetch
        </h3>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Articles per run
            </label>
            <select
              value={maxArticles}
              onChange={(e) => setMaxArticles(Number(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-2 text-sm"
            >
              {FETCH_DEPTHS.map((d) => (
                <option key={d.articles} value={d.articles}>
                  {d.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              ≈{FETCH_DEPTHS.find((d) => d.articles === maxArticles)?.pages ?? 4}{' '}
              GNews request(s) per search. Hit this cap and the run is a
              truncated slice of the window, newest first.
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              How far back to look
            </label>
            <select
              value={lookback}
              onChange={(e) => setLookback(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-2 text-sm"
            >
              {LOOKBACKS.map((l) => (
                <option key={l.key} value={l.key}>
                  {l.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {lookback === 'auto'
                ? 'Everything published since the last run - no gaps, no repeats.'
                : 'A fixed window. Shorter than the gap between runs and news published in between is never seen.'}
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Judging rounds per run
            </label>
            <select
              value={maxPasses}
              onChange={(e) => setMaxPasses(Number(e.target.value))}
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-2 text-sm"
            >
              {[2, 4, 6, 8, 12, 20].map((n) => (
                <option key={n} value={n}>
                  {n} rounds
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              The agent judges about 25 articles a round. Too few and the rest
              come back as <strong>not reviewed</strong>.
            </p>
          </div>
        </div>
        {maxPasses * 25 < maxArticles && (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            {maxPasses} rounds can reach about {maxPasses * 25} articles, but this
            beat fetches up to {maxArticles}. The remainder will be left unreviewed.
          </p>
        )}
      </section>

      <section>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          SEO project <span className="font-normal">(optional)</span>
        </label>
        <select
          value={projectId ?? ''}
          onChange={(e) => setProjectId(e.target.value ? Number(e.target.value) : null)}
          className="w-full sm:w-80 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md px-2 py-2 text-sm"
        >
          <option value="">Not linked</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Linking borrows the project's context for suggestions. The beat also
          pauses when that project is paused.
        </p>
      </section>

      {/* WHERE THE LOOP CLOSES. The run controls live at the top of this form,
          which is nowhere near the attribute editor you were just in — so a
          settings change ended with a "Saved" chip and no hint that anything
          could be done with it. The offer belongs at the moment of the change,
          not in a toolbar you have already scrolled past. */}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {!dirty && savedAt && (
          <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved</span>
        )}
        {!dirty && savedAt && lastBriefingId && (
          <>
            {beat.mode === 'DRAFT' && onReprocess ? (
              <>
                <button
                  onClick={() => onReprocess(lastBriefingId)}
                  disabled={runState === 'running'}
                  className="px-3 py-2 text-sm rounded-md border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 disabled:opacity-50"
                >
                  Re-judge the same articles
                </button>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Clears the last run's stories and judges its articles again
                  with these settings. No new search.
                </span>
              </>
            ) : (
              beat.mode === 'LIVE' && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  To try these settings against the articles you already have,
                  put the beat in <strong>draft</strong> - the button is next to
                  its name.
                </span>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
}
